import LeadData from "./LeadData";
import {LeadApiDto} from "../../api/model/rest";
import * as moment from "moment";
import {none, Option, option} from "ts-option";
import DateUtils from "../../utils/DateUtils";
import TrackingData from "./TrackingData";
import Tag from "../tag/Tag";
import User from "../user/User";
import {getUser} from "../../store/view/user";
import LeadAction from "./LeadAction";
import {getLeadStatus} from "../../store/view/leadStatus";

export default class Lead {
	/**
	 * String using to search a lead.
	 */
	public readonly searchString: string;

	/**
	 * Lead object.
	 * @param {string} id identifier of the lead.
	 * @param {string} statusId identifier of the lead status.
	 * @param {LeadData} data data of the lead.
	 * @param {ProductEligibilityData} productEligibilityData product eligibility data.
	 * @param {moment.Moment} creationDate lead creation date.
	 * @param {moment.Moment} updateDateTime lead last update date.
	 * @param {referenceNumber} referenceNumber reference number of lead
	 * @param {string[]} watchers array of user id's.
	 * @param  {string[]} tags array with tags
	 * @param  {Option<string>} agentId assigned lead agent.
	 * @param  {Option<LeadAction>} lastAction last lead action
	 * @param  {Option<string>} preferredMerchantBranchLocationId identifier of merchant branch location.
	 */
	constructor(public readonly id: string,
		public readonly statusId: string,
		public readonly data: LeadData,
		public readonly creationDate: moment.Moment,
		public readonly updateDateTime: Option<moment.Moment>,
		public readonly referenceNumber: string,
		public readonly watchers: string[],
		public readonly tags: Tag[],
		public readonly agentId: Option<string>,
		public readonly lastAction: Option<LeadAction>,
		public readonly preferredMerchantBranchLocationId: Option<string>) {

		this.searchString = `${referenceNumber} ${creationDate.format(DateUtils.DATE_TIME_FORMAT)}`.toLowerCase();
	}

	static fromJson(json: LeadApiDto): Lead {
		const trackingData = json.tracking ? option(TrackingData.fromJson(json.tracking)) : none;
		const leadData = new LeadData(
			option(json.siteId),
			json.contactId,
			option(json.description),
			option(json.extendedDescription),
			json.categories,
			option(json.acquisitionChannelId),
			json.leadTypeId,
			trackingData,
			json.payByFinance
		);
		const updateDateTime = json.updateDateTime ? option(moment(json.updateDateTime)) : none;
		const tags = json.tags ? json.tags.map(i => new Tag(i)) : [];
		const lastAction = json.lastActivity ? option(LeadAction.fromString(json.lastActivity)) : none;
		return new Lead(json.id, json.statusId, leadData, moment(json.creationDate), updateDateTime,
			json.referenceNumber, json.watchers, tags, option(json.agentId), lastAction,
			option(json.preferredMerchantBranchLocationId));
	}

	/**
	 * Get lead Agent.
	 * @returns {Option<User>}
	 */
	getAgent(): Option<User> {
		return this.agentId.flatMap(getUser);
	}

	/**
	 * Get lead status label.
	 * @returns {string}
	 */
	getStatusLabel(): string {
		return getLeadStatus(this.statusId).map(s => s.getStatusLabel()).getOrElse("");
	}

	copy({
		id = this.id,
		statusId = this.statusId,
		data = this.data,
		creationDate = this.creationDate,
		updateDateTime = this.updateDateTime,
		referenceNumber = this.referenceNumber,
		watchers = this.watchers,
		tags = this.tags,
		agentId = this.agentId,
		lastAction = this.lastAction,
		preferredMerchantBranchLocationId = this.preferredMerchantBranchLocationId
	}): Lead {
		return new Lead(id,
			statusId,
			data,
			creationDate,
			updateDateTime,
			referenceNumber,
			watchers,
			tags,
			agentId,
			lastAction,
			preferredMerchantBranchLocationId);
	}
}